import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCourse } from '../../contexts/CourseContext';
import { SchoolOutlined, AddOutlined, SearchOutlined } from '../../icons';
import DashboardTopbar from '../../components/DashboardTopbar';
import PlansModal from '../../components/PlansModal';
import './CourseSelection.css';

// Tela de escolha do concurso ativo (1ª vez, ou quando o salvo não existe
// mais). A escolha de matérias/tópicos e o plano de estudos (horas/dias)
// ficam a cargo da tela /selecionar-materias — "Criar meu concurso" já cria
// o concurso (com um nome padrão, editável lá) e navega direto pra ela, sem
// passo intermediário.
function CourseSelection() {
  const navigate = useNavigate();
  const courseCtx = useCourse();
  const [search, setSearch] = useState('');
  const [creatingCourse, setCreatingCourse] = useState(false);
  const [createError, setCreateError] = useState('');
  const [failedImageIds, setFailedImageIds] = useState(() => new Set());
  const [showPlansModal, setShowPlansModal] = useState(false);

  const courses = courseCtx?.courses;
  const registeredCourses = courseCtx?.registeredCourses;
  const loadingCourses = courseCtx?.loadingCourses;

  const filteredCourses = useMemo(() => {
    // Concursos que o usuário já tem (personalizados ou com plano gerado)
    // não aparecem aqui — essa tela é só pra adicionar um concurso novo; pra
    // trocar entre os que já tem, o dropdown da home (CourseSwitcher) resolve.
    const registeredIds = new Set((registeredCourses || []).map((c) => c.id));
    const list = (courses || []).filter((course) => !registeredIds.has(course.id));
    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter((course) => course.name.toLowerCase().includes(term));
  }, [courses, registeredCourses, search]);

  const markImageFailed = (id) => {
    setFailedImageIds((prev) => new Set(prev).add(id));
  };

  const selectCourse = (course) => {
    courseCtx.setActiveCourseId(course.id);
    // Sem replace: precisa ficar no histórico pra "voltar" do navegador
    // sair de /selecionar-materias direto pra essa tela de escolha.
    navigate(course.hasPlan ? '/simulados' : '/selecionar-materias');
  };

  const createCourse = async () => {
    setCreatingCourse(true);
    setCreateError('');
    try {
      await courseCtx.createCustomCourse('Meu concurso', '');
      navigate('/selecionar-materias');
    } catch (error) {
      if (error.response?.status === 403 && error.response.data?.code === 'PLAN_LIMIT_REACHED') {
        setShowPlansModal(true);
      } else {
        setCreateError(error.response?.data?.message || 'Erro ao criar concurso. Tente novamente.');
      }
      setCreatingCourse(false);
    }
  };

  if (!courseCtx || loadingCourses) {
    return (
      <div className="App dashboard-page course-selection-page">
        <DashboardTopbar label="Escolher concurso" hideCourseSwitcher />
        <div className="loading-message">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="App dashboard-page course-selection-page">
      {showPlansModal && (
        <PlansModal
          trackingPrefix="course_limit_wall"
          heroTitle="Seu plano grátis permite 1 concurso"
          heroSubtitle="Libere acesso completo para estudar para quantos concursos quiser, com simulados e materiais de cada um."
          onClose={() => navigate('/simulados', { replace: true })}
        />
      )}
      <DashboardTopbar label="Escolher concurso" hideCourseSwitcher />
      <div className="course-selection-card">
        <div className="course-selection-header">
          <h2 className="course-selection-title">Para qual concurso você está estudando?</h2>
          <p className="course-selection-subtitle">
            Busque pelo nome ou crie um concurso personalizado.
          </p>
        </div>

        <div className="course-selection-search">
          <SearchOutlined style={{ fontSize: 18 }} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar concurso…"
          />
        </div>

        <div className="course-selection-options">
          {filteredCourses.map((course) => (
            <button
              type="button"
              key={course.id}
              className="course-selection-option"
              onClick={() => selectCourse(course)}
            >
              {course.image_url && !failedImageIds.has(course.id) ? (
                <img
                  src={course.image_url}
                  alt=""
                  className="course-selection-option-image"
                  onError={() => markImageFailed(course.id)}
                />
              ) : (
                <span className="course-selection-option-icon">
                  <SchoolOutlined style={{ fontSize: 20 }} />
                </span>
              )}
              <span className="course-selection-option-text">
                <strong>{course.name}</strong>
                <span>
                  {course.subjectsCount || 0} matéria{course.subjectsCount === 1 ? '' : 's'} no edital
                </span>
              </span>
              <span className="course-selection-option-arrow">›</span>
            </button>
          ))}

          {filteredCourses.length === 0 && (
            <div className="course-selection-empty">
              {search.trim()
                ? `Nenhum concurso encontrado para "${search.trim()}".`
                : 'Nenhum concurso catalogado ainda. Que tal criar o seu?'}
            </div>
          )}

          <button
            type="button"
            className="course-selection-option course-selection-option-create"
            onClick={createCourse}
            disabled={creatingCourse}
          >
            <span className="course-selection-option-icon course-selection-option-icon-create">
              <AddOutlined style={{ fontSize: 20 }} />
            </span>
            <span className="course-selection-option-text">
              <strong>{creatingCourse ? 'Criando...' : 'Criar meu concurso'}</strong>
              <span>Defina suas próprias matérias e tópicos</span>
            </span>
            <span className="course-selection-option-arrow">›</span>
          </button>
          {createError && <div className="course-selection-empty">{createError}</div>}
        </div>
      </div>
    </div>
  );
}

export default CourseSelection;
